import { CONCEPT_MAP } from "../data/concepts";
import { ALIAS_IDS, analects, analectById, canonicalId } from "./analects";
import { conceptsOf } from "./conceptTag";
import { GATE_ORDER, gateById } from "./gates";
import { dayKeyOf, emptyDaily, prevDayKey, questsAllDone } from "./quests";
import { emptySrs, gateDue, gatePassed, overdueDays, reviewGate } from "./srs";
import type { Analect, GateId, GateSrs, MissEntry, ProgressState, QuoteProgress, VirtueId } from "./types";

export const STORAGE_KEY = "lunyu-battle-progress-v1";
export const STATE_VERSION = 2;
const DAY = 86_400_000;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function emptyVirtues(): Record<VirtueId, number> {
  return { ren: 0, li: 0, zhi: 0, xin: 0, yong: 0 };
}

function emptyGates(): Record<GateId, GateSrs> {
  return { meaning: emptySrs(), misread: emptySrs(), context: emptySrs(), recall: emptySrs() };
}

export function emptyRecord(): QuoteProgress {
  return { gates: emptyGates() };
}

export function createInitialProgress(): ProgressState {
  return {
    version: STATE_VERSION,
    records: {},
    virtues: emptyVirtues(),
    streak: 0,
    bestStreak: 0,
    focus: 100,
    updatedAt: Date.now(),
    daily: emptyDaily(),
    dayStreak: 0,
    bestDayStreak: 0,
    lastDayKey: "",
    misses: [],
  };
}

export function getRecord(state: ProgressState, id: number): QuoteProgress {
  return state.records[String(id)] ?? emptyRecord();
}

// ---- persistence + migration -------------------------------------------------

type LegacyGate = boolean;
interface LegacyRecord {
  gates?: Record<string, LegacyGate>;
  lastSeen?: number;
  lastWrongGate?: GateId;
}

/**
 * The source corpus repeated 29 passages (鄉黨 10.25–10.27 and all of 先進),
 * so a save file may hold progress under a duplicate's id. Fold those onto the
 * surviving id, keeping the better of the two records per gate, rather than
 * silently dropping work the student already did.
 */
function foldAliasRecords(records: Record<string, QuoteProgress>): Record<string, QuoteProgress> {
  if (!ALIAS_IDS.size) return records;
  const out: Record<string, QuoteProgress> = {};
  for (const [key, record] of Object.entries(records)) {
    if (!record?.gates) continue;
    const target = String(canonicalId(Number(key)));
    const existing = out[target];
    if (!existing) {
      out[target] = record;
      continue;
    }
    const gates = { ...existing.gates };
    for (const gate of GATE_ORDER) {
      const a = existing.gates[gate];
      const b = record.gates[gate];
      if (!a) gates[gate] = b;
      else if (b && (b.reps > a.reps || (b.reps === a.reps && b.correct > a.correct))) gates[gate] = b;
    }
    out[target] = {
      gates,
      lastWrongGate: existing.lastWrongGate ?? record.lastWrongGate,
      masteredAt: Math.min(existing.masteredAt ?? Infinity, record.masteredAt ?? Infinity) || undefined,
    };
  }
  return out;
}

function migrate(raw: unknown): ProgressState {
  if (!raw || typeof raw !== "object") return createInitialProgress();
  const data = raw as Record<string, unknown>;

  if (data.version === STATE_VERSION && data.records) {
    const next: ProgressState = {
      ...createInitialProgress(),
      ...(data as unknown as ProgressState),
      virtues: { ...emptyVirtues(), ...((data.virtues as Record<VirtueId, number>) ?? {}) },
      records: foldAliasRecords((data.records as Record<string, QuoteProgress>) ?? {}),
    };
    if (!next.daily || typeof next.daily.dayKey !== "string") next.daily = emptyDaily();
    if (!Array.isArray(next.misses)) next.misses = [];
    next.misses = next.misses
      .filter((miss) => miss && typeof miss.id === "number")
      .map((miss) => ({ ...miss, id: canonicalId(miss.id) }));
    return next;
  }

  // v1 (boolean gates, no SRS) → v2
  const now = Date.now();
  const records: Record<string, QuoteProgress> = {};
  const legacy = (data.records as Record<string, LegacyRecord>) ?? {};
  for (const [id, rec] of Object.entries(legacy)) {
    const oldGates = rec?.gates ?? {};
    const gates = emptyGates();
    let passed = 0;
    GATE_ORDER.forEach((gate, index) => {
      if (oldGates[gate] === true) {
        passed += 1;
        const spread = 1 + ((Number(id) + index) % 4); // 1..4 days, staggered
        gates[gate] = { reps: 1, ease: 2.3, interval: 3, dueAt: now + spread * DAY, attempts: 1, correct: 1 };
      }
    });
    records[id] = {
      gates,
      lastWrongGate: rec?.lastWrongGate,
      masteredAt: passed === GATE_ORDER.length ? Number(rec?.lastSeen) || now : undefined,
    };
  }

  return {
    version: STATE_VERSION,
    records,
    virtues: emptyVirtues(),
    streak: Number(data.streak) || 0,
    bestStreak: Number(data.bestStreak) || 0,
    focus: clamp(Number(data.focus) || 100, 0, 100),
    updatedAt: now,
    daily: emptyDaily(),
    dayStreak: 0,
    bestDayStreak: 0,
    lastDayKey: "",
    misses: [],
  };
}

export function loadProgress(): ProgressState {
  try {
    return migrate(JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"));
  } catch {
    return createInitialProgress();
  }
}

export function saveProgress(state: ProgressState): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, updatedAt: Date.now() }));
    return true;
  } catch {
    /* storage may be unavailable (private mode); progress stays in memory */
    return false;
  }
}

export function resetProgress(): ProgressState {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  return createInitialProgress();
}

export function exportProgress(state: ProgressState): void {
  const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `lunyu-battle-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function importProgress(text: string): ProgressState {
  return migrate(JSON.parse(text));
}

// ---- queries -----------------------------------------------------------------

export function countPassedGates(record: QuoteProgress): number {
  return GATE_ORDER.filter((gate) => gatePassed(record.gates[gate])).length;
}

export function countDueGates(record: QuoteProgress, now: number = Date.now()): number {
  return GATE_ORDER.filter((gate) => gateDue(record.gates[gate], now)).length;
}

export function isMastered(record: QuoteProgress): boolean {
  return countPassedGates(record) === GATE_ORDER.length;
}

export function nextOpenGate(record: QuoteProgress, now: number = Date.now()): GateId {
  return (
    GATE_ORDER.find((gate) => !gatePassed(record.gates[gate])) ??
    GATE_ORDER.find((gate) => gateDue(record.gates[gate], now)) ??
    "meaning"
  );
}

export interface ProgressSummary {
  mastered: number;
  total: number;
  gates: number;
  totalGates: number;
  due: number;
  percent: number;
}

export function progressSummary(state: ProgressState, now: number = Date.now()): ProgressSummary {
  let mastered = 0;
  let gates = 0;
  let due = 0;
  for (const analect of analects) {
    const record = getRecord(state, analect.id);
    const passed = countPassedGates(record);
    gates += passed;
    if (passed === GATE_ORDER.length) mastered += 1;
    due += countDueGates(record, now);
  }
  const totalGates = analects.length * GATE_ORDER.length;
  return {
    mastered,
    total: analects.length,
    gates,
    totalGates,
    due,
    percent: Math.round((gates / totalGates) * 100),
  };
}

export function chapterSummary(state: ProgressState, major: number) {
  const items = analects.filter((item) => item.major === major);
  let mastered = 0;
  let gates = 0;
  for (const item of items) {
    const record = getRecord(state, item.id);
    const passed = countPassedGates(record);
    gates += passed;
    if (passed === GATE_ORDER.length) mastered += 1;
  }
  const totalGates = items.length * GATE_ORDER.length;
  return {
    total: items.length,
    mastered,
    gatePercent: items.length ? Math.round((gates / totalGates) * 100) : 0,
  };
}

export function deckStrength(state: ProgressState): number {
  return Object.values(state.virtues).reduce((sum, value) => sum + value, 0);
}

/** Higher as the player advances and as more gates of this quote are passed. */
export function difficultyFor(state: ProgressState, analect: Analect): number {
  const global = progressSummary(state).percent / 100;
  const passed = countPassedGates(getRecord(state, analect.id)) / GATE_ORDER.length;
  return clamp(0.25 + global * 0.5 + passed * 0.25, 0, 1);
}

// ---- mutation ----------------------------------------------------------------

export interface AnswerOutcome {
  state: ProgressState;
  correct: boolean;
  newlyMastered: boolean;
  virtue: VirtueId;
  virtueGain: number;
  questsJustDone: boolean;
}

const MISS_CAP = 60;

function updateMisses(misses: MissEntry[], id: number, gate: GateId, correct: boolean, now: number): MissEntry[] {
  const rest = (misses ?? []).filter((miss) => !(miss.id === id && miss.gate === gate));
  if (correct) return rest;
  return [{ id, gate, at: now }, ...rest].slice(0, MISS_CAP);
}

export function applyAnswer(
  state: ProgressState,
  analect: Analect,
  gate: GateId,
  correct: boolean,
  now: number = Date.now(),
): AnswerOutcome {
  const record = getRecord(state, analect.id);
  const wasMastered = isMastered(record);
  const wasDue = gateDue(record.gates[gate], now);
  const gates = { ...record.gates, [gate]: reviewGate(record.gates[gate], correct, now) };
  const nextRecord: QuoteProgress = {
    ...record,
    gates,
    lastWrongGate: correct ? record.lastWrongGate : gate,
  };
  const newlyMastered = countPassedGates(nextRecord) === GATE_ORDER.length && !wasMastered;
  if (newlyMastered) nextRecord.masteredAt = now;

  const virtue = virtueOf(analect);
  const gain = correct ? gateById(gate).damage : 0;
  const virtues = { ...state.virtues };
  if (gain) virtues[virtue] += gain;

  const streak = correct ? state.streak + 1 : 0;

  // 連續修業 — one answer a day keeps the day streak alive.
  const dayKey = dayKeyOf(now);
  let dayStreak = state.dayStreak;
  if (state.lastDayKey !== dayKey) {
    dayStreak = state.lastDayKey === prevDayKey(dayKey) ? state.dayStreak + 1 : 1;
  }

  // 今日修業 counters (reset when the day rolls over).
  const prevDaily = state.daily && state.daily.dayKey === dayKey ? state.daily : emptyDaily(dayKey);
  const daily = {
    ...prevDaily,
    correct: prevDaily.correct + (correct ? 1 : 0),
    mastered: prevDaily.mastered + (newlyMastered ? 1 : 0),
    bestCombo: Math.max(prevDaily.bestCombo, streak),
    reviews: prevDaily.reviews + (correct && wasDue ? 1 : 0),
  };
  const questsJustDone = !prevDaily.questsDoneAt && questsAllDone(daily);
  if (questsJustDone) daily.questsDoneAt = now;

  return {
    state: {
      ...state,
      records: { ...state.records, [String(analect.id)]: nextRecord },
      virtues,
      streak,
      bestStreak: Math.max(state.bestStreak, streak),
      focus: clamp(state.focus + (correct ? 4 : -14), 0, 100),
      updatedAt: now,
      daily,
      dayStreak,
      bestDayStreak: Math.max(state.bestDayStreak, dayStreak),
      lastDayKey: dayKey,
      misses: updateMisses(state.misses, analect.id, gate, correct, now),
    },
    correct,
    newlyMastered,
    virtue,
    virtueGain: gain,
    questsJustDone,
  };
}

/** The 五德 rail is fed by the passage's leading concept. */
export function virtueOf(analect: Analect): VirtueId {
  const first = conceptsOf(analect)[0];
  return CONCEPT_MAP.get(first)?.virtue ?? "zhi";
}

// ---- selection ---------------------------------------------------------------

export type NextMode = "auto" | "new" | "review" | "wrong";
export type ChapterFilter = number | "all";

function maxOverdue(state: ProgressState, analect: Analect, now: number): number {
  const record = getRecord(state, analect.id);
  return Math.max(...GATE_ORDER.map((gate) => overdueDays(record.gates[gate], now)));
}

function scoreCandidate(state: ProgressState, analect: Analect, now: number): number {
  const record = getRecord(state, analect.id);
  const attempts = GATE_ORDER.reduce((sum, gate) => sum + record.gates[gate].attempts, 0);
  if (attempts === 0) return 10_000 - analect.id; // fresh material first, in book order
  const missing = GATE_ORDER.length - countPassedGates(record);
  const errorBoost = record.lastWrongGate ? 8 : 0;
  const recency = Math.min(1, (now - (record.masteredAt ?? 0)) / (7 * DAY));
  return missing * 100 + errorBoost + recency;
}

export function chooseNext(
  state: ProgressState,
  opts: { currentId?: number; chapter?: ChapterFilter; mode?: NextMode } = {},
): Analect {
  const mode = opts.mode ?? "auto";
  const chapter = opts.chapter ?? "all";
  const now = Date.now();

  const pool = analects.filter((analect) => {
    if (analect.id === opts.currentId && analects.length > 1) return false;
    if (chapter !== "all" && analect.major !== chapter) return false;
    return true;
  });
  const safePool = pool.length ? pool : analects;

  if (mode === "wrong") {
    // 攻錯 — revisit the most recent misses first; fall through when empty.
    const poolIds = new Set(safePool.map((analect) => analect.id));
    for (const miss of state.misses ?? []) {
      if (!poolIds.has(miss.id)) continue;
      const item = analectById.get(miss.id);
      if (item) return item;
    }
  }

  if (mode !== "new") {
    const dueItems = safePool
      .filter((analect) => countDueGates(getRecord(state, analect.id), now) > 0)
      .sort((a, b) => maxOverdue(state, b, now) - maxOverdue(state, a, now));
    if (dueItems.length) return dueItems[0];
    if (mode === "review") {
      // nothing due — fall back to unfinished material
    }
  }

  const open = safePool.filter((analect) => !isMastered(getRecord(state, analect.id)));
  const candidates = open.length ? open : safePool;
  return (
    candidates.slice().sort((a, b) => scoreCandidate(state, b, now) - scoreCandidate(state, a, now))[0] ??
    analects[0]
  );
}
